import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { TicketIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FidelityRedemptions = () => {
  const { token } = useAuth();
  const [redemptions, setRedemptions] = useState([]);
  const [filteredRedemptions, setFilteredRedemptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'pending', 'completed'

  useEffect(() => {
    loadRedemptions();
  }, []);

  useEffect(() => {
    filterRedemptions();
  }, [statusFilter, redemptions]);

  const loadRedemptions = async () => {
    try {
      const response = await axios.get(`${API}/fidelity/redemptions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRedemptions(response.data);
      setFilteredRedemptions(response.data);
    } catch (error) {
      console.error('Errore caricamento riscatti:', error);
      toast.error('Errore nel caricamento dei riscatti');
    } finally {
      setLoading(false);
    }
  };

  const filterRedemptions = () => {
    if (statusFilter === 'all') {
      setFilteredRedemptions(redemptions);
    } else {
      setFilteredRedemptions(redemptions.filter(r => r.status === statusFilter));
    }
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('it-IT');
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { variant: 'secondary', label: 'In Attesa', color: 'text-yellow-600' },
      completed: { variant: 'default', label: 'Completato', color: 'text-green-600' },
      cancelled: { variant: 'destructive', label: 'Annullato', color: 'text-red-600' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    return (
      <Badge variant={config.variant}>
        {config.label}
      </Badge>
    );
  };

  const stats = {
    total: redemptions.length,
    pending: redemptions.filter(r => r.status === 'pending').length,
    completed: redemptions.filter(r => r.status === 'completed').length,
    totalPoints: redemptions.reduce((sum, r) => sum + r.points_spent, 0)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Caricamento riscatti...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Gestione Riscatti</h1>
        <p className="text-gray-600">Visualizza e gestisci i premi riscattati dai clienti</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-gray-600">Riscatti Totali</p>
              <p className="text-3xl font-bold text-blue-600">{stats.total}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-gray-600">In Attesa</p>
              <p className="text-3xl font-bold text-yellow-600">{stats.pending}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-gray-600">Completati</p>
              <p className="text-3xl font-bold text-green-600">{stats.completed}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-gray-600">Punti Riscattati</p>
              <p className="text-3xl font-bold text-purple-600">{stats.totalPoints.toLocaleString()}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <FunnelIcon className="w-5 h-5 text-gray-600" />
            <div className="flex gap-2">
              <button
                onClick={() => setStatusFilter('all')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  statusFilter === 'all'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Tutti ({redemptions.length})
              </button>
              <button
                onClick={() => setStatusFilter('pending')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  statusFilter === 'pending'
                    ? 'bg-yellow-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                In Attesa ({stats.pending})
              </button>
              <button
                onClick={() => setStatusFilter('completed')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  statusFilter === 'completed'
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Completati ({stats.completed})
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Redemptions List */}
      <div className="space-y-4">
        {filteredRedemptions.map((redemption) => (
          <Card key={redemption.id}>
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-bold text-gray-900">
                      {redemption.reward_name}
                    </h3>
                    {getStatusBadge(redemption.status)}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Cliente:</span>
                      <p className="font-medium">{redemption.customer_name}</p>
                      <p className="text-xs text-gray-500">Carta: {redemption.customer_code}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">Data Riscatto:</span>
                      <p className="font-medium">{formatDateTime(redemption.created_at)}</p>
                    </div>
                    {redemption.notes && (
                      <div className="md:col-span-2">
                        <span className="text-gray-600">Note:</span>
                        <p className="font-medium">{redemption.notes}</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="text-right ml-4">
                  <div className="text-sm text-gray-600 mb-1">Punti Spesi</div>
                  <div className="text-2xl font-bold text-red-600">
                    -{redemption.points_spent}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {filteredRedemptions.length === 0 && (
          <Card>
            <CardContent className="py-16 text-center">
              <TicketIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Nessun riscatto trovato
              </h3>
              <p className="text-sm text-gray-600">
                {statusFilter === 'all' 
                  ? 'Non ci sono ancora riscatti effettuati'
                  : `Nessun riscatto con stato "${statusFilter}"`
                }
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default FidelityRedemptions;
